import React, { useState, useEffect, useRef, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, NavLink, useLocation, useParams, Navigate, useNavigate } from 'react-router-dom';
import {
  Send, LayoutDashboard, Kanban, Users, Waves, Wrench, Database,
  LogOut, Search, ChevronDown, Menu, MapPin, Gavel, X,
  Cpu, Lightbulb,
} from 'lucide-react';
import { ToastProvider } from './components/Toast';
import { ConfirmProvider } from './components/ConfirmDialog';
import ThemeToggle from './components/ThemeToggle';
import CommandPalette from './components/CommandPalette';
import { getTheme, applyTheme, getInitialTheme } from './theme/theme';
import { ArcReactor, ArcLoader } from './components/hud';
import { STATE_NAMES } from './utils/usStates';
import Login from './pages/Login'; // eager — the pre-auth screen must render instantly
import ErrorBoundary from './components/ErrorBoundary';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { getSavedViews, removeView, onSavedViewsChange } from './utils/savedViews';
import { homeFor, navFor, isProducer } from './utils/roleAccess';
import JarvisDock from './components/jarvis/JarvisDock';

// Every page is lazy-loaded so the initial bundle carries only the shell +
// login. Heavy dependencies ride along with their own page and are fetched on
// first navigation there: @react-pdf/renderer (~3MB) with the bond pages,
// Leaflet with the map. Route chunks are cached by the browser after first visit.
// After a deploy, an open tab's cached index.html can reference the OLD chunk
// filenames, so a lazy import() 404s and the page can't load (this blanked the
// app). lazyWithReload forces exactly ONE hard reload to fetch the fresh build,
// which resolves the stale-chunk case automatically. A per-load sessionStorage
// flag prevents an infinite reload loop if the failure is something else.
function lazyWithReload(importer) {
  return lazy(() =>
    importer().catch((err) => {
      const KEY = 'chunk-reload-once';
      if (!sessionStorage.getItem(KEY)) {
        sessionStorage.setItem(KEY, '1');
        window.location.reload();
        // Return a never-resolving promise so nothing renders before reload.
        return new Promise(() => {});
      }
      throw err; // already reloaded once — let the error boundary show it
    })
  );
}
// Clear the guard after a successful load so a future deploy can reload again.
window.addEventListener('load', () => {
  try { sessionStorage.removeItem('chunk-reload-once'); } catch (e) { /* ignore */ }
});

const SouthFlorida = lazyWithReload(() => import('./pages/SouthFlorida'));
const ProducerContractor = lazyWithReload(() => import('./pages/ProducerContractor'));
const Dashboard = lazyWithReload(() => import('./pages/Dashboard'));
const DOTPortal = lazyWithReload(() => import('./pages/DOTPortal'));
const Contractors = lazyWithReload(() => import('./pages/Contractors'));
const ContractorProfile = lazyWithReload(() => import('./pages/ContractorProfile'));
const ContractorMap = lazyWithReload(() => import('./pages/ContractorMap'));
const LettingDetail = lazyWithReload(() => import('./pages/LettingDetail'));
const BidResults = lazyWithReload(() => import('./pages/BidResults'));
const BondForms = lazyWithReload(() => import('./pages/BondForms'));
const BondFormNew = lazyWithReload(() => import('./pages/BondFormNew'));
const BondFormEditor = lazyWithReload(() => import('./pages/BondFormEditor'));
const BondTools = lazyWithReload(() => import('./pages/BondTools'));
const JarvisPage = lazyWithReload(() => import('./pages/Jarvis'));
const JarvisMemory = lazyWithReload(() => import('./pages/JarvisMemory'));
const JarvisVoice = lazyWithReload(() => import('./pages/JarvisVoice'));
const Enhancements = lazyWithReload(() => import('./pages/Enhancements'));
const BondCalculator = lazyWithReload(() => import('./pages/BondCalculator'));
const BondLetters = lazyWithReload(() => import('./pages/BondLetters'));
const WipAnalyzer = lazyWithReload(() => import('./pages/WipAnalyzer'));
const FinancialReview = lazyWithReload(() => import('./pages/FinancialReview'));
const ArTracker = lazyWithReload(() => import('./pages/ArTracker'));
const Pipeline = lazyWithReload(() => import('./pages/Pipeline'));
const AdminDataSources = lazyWithReload(() => import('./pages/AdminDataSources'));
const ImportContractors = lazyWithReload(() => import('./pages/ImportContractors'));
const OutreachWorkspace = lazyWithReload(() => import('./pages/OutreachWorkspace'));
const Team = lazyWithReload(() => import('./pages/Team'));

// Smart view presets for Contractors sidebar
const SMART_VIEWS = [
  { section: 'PIPELINE', items: [
    { key: 'all', label: 'All Contractors', countKey: 'all', params: {} },
    { key: 'new', label: 'New This Week', countKey: 'new_this_week', params: { is_new: '1', sort_by: 'first_bid_date', sort_dir: 'desc' } },
    { key: 'hot', label: 'Hot Leads', countKey: 'hot_leads', countClass: 'hot', params: { lead_priority: 'hot', is_client: '0', sort_by: 'lead_score', sort_dir: 'desc' } },
  ]},
  { section: 'PROSPECTING', items: [
    { key: 'active', label: 'Active Bidders', params: { has_bids: '1', is_client: '0', sort_by: 'total_bids', sort_dir: 'desc' } },
    { key: 'winners', label: 'Frequent Winners', params: { has_bids: '1', sort_by: 'wins', sort_dir: 'desc' } },
    { key: 'dbe', label: 'DBE Certified', countKey: 'dbe', params: { dbe_status: '1' } },
    { key: 'planholders', label: 'Planholders only', params: { planholders_only: '1', sort_by: 'first_bid_date', sort_dir: 'desc' } },
    // Companies known from municipal permit feeds (Orlando commercial permits).
    // Permits are not bids — they get their own lane instead of masquerading
    // as bidding activity (see server/utils/bid-filters.js NOT_A_BID).
    { key: 'permit_pullers', label: 'Permit Pullers', params: { permit_pullers: '1', sort_by: 'first_bid_date', sort_dir: 'desc' } },
    { key: 'prospects', label: 'Not Yet Clients', params: { is_client: '0', has_bids: '1', has_phone: '1', sort_by: 'total_bids', sort_dir: 'desc' } },
    { key: 'reviewed_not_client', label: 'Reviewed – Not Client', countKey: 'reviewed_not_client', params: { is_client: '0', reviewed: '1', has_bids: '1', sort_by: 'lead_score', sort_dir: 'desc' } },
  ]},
  { section: 'BOOK OF BUSINESS', items: [
    { key: 'clients', label: 'My Clients', countKey: 'clients', countClass: 'client', params: { is_client: '1' } },
    { key: 'client_activity', label: 'Client Activity', params: { is_client: '1', has_bids: '1', sort_by: 'total_bids', sort_dir: 'desc' } },
  ]},
  { section: 'DATA QUALITY', items: [
    { key: 'no_contact', label: 'Needs Contact Info', countKey: 'needs_contact', params: { has_phone: '0', has_email: '0', sort_by: 'lead_score', sort_dir: 'desc' } },
  ]},
];

/**
 * Bid Results presets. Same shape as SMART_VIEWS.
 *
 * Deliberately NOT state-scoped: /bid-results has never gone through
 * withState() because the page is national by design — only about a third of
 * the lettings carrying bids are Florida.
 *
 * DOT Portal used to hang off the bottom of this list under a DEEP DIVE
 * divider. It is no longer offered in the nav — the FDOT district rail and
 * plan-puller views were not being used. The PAGE still exists and /dot-portal
 * still resolves, because four retired routes redirect into it
 * (/opportunities, /lettings, /plan-pullers, /analytics); removing it outright
 * would break all four. It is simply no longer surfaced.
 */
const BID_VIEWS = [
  { items: [
    { key: 'all', label: 'All Results', params: {} },
    // Two statuses, which is why the server's bid_status filter takes a list.
    { key: 'rejected', label: 'Rejected & Lost', params: { mode: 'bidders', bid_status: 'rejected,non-responsive' } },
  ] },
];

/** Data-entry and system administration. Admin-only, section and all. */
const ADMIN_VIEWS = [
  { items: [
    { key: 'data-sources', label: 'Data Sources', to: '/data-sources', keepState: true, countKey: 'scraperAlerts', countClass: 'hot' },
    { key: 'import', label: 'Import CSV', to: '/import-contractors', keepState: true },
    { key: 'team', label: 'Team', to: '/team', keepState: true },
  ] },
];

/**
 * /today was the home page for months, so it is bookmarked, typed, and linked
 * from toasts. It is a tab of the Command Center now; redirect rather than drop
 * it, preserving ?state=. `replace` keeps it out of history so Back does not
 * bounce between the two URLs.
 */
function TodayRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/dashboard${search}`} replace />;
}

const ADMIN_PATHS = ['/data-sources', '/import-contractors', '/team'];
// Only what the expander actually links to. /dot-portal is still routed but
// no longer offered here, so it must not light this section up.
const BID_PATHS = ['/bid-results'];

/**
 * A querystring change does not remount a route. Without this key, clicking a
 * preset while already on Bid Results would leave the previous view's filters,
 * page number and expanded row in place — and the page's URL-sync effect would
 * then write those stale filters straight back over the new URL.
 */
function BidResultsRoute() {
  const { search } = useLocation();
  return <BidResults key={new URLSearchParams(search).get('preset') || ''} />;
}

/**
 * Same reason, keyed on the id: clicking a competitor goes A → B on the same
 * route, and a reused profile kept A's edit form and Pinned Notes draft — whose
 * autosave then wrote A's notes into B.
 */
function ContractorProfileRoute() {
  const { id } = useParams();
  return <ContractorProfile key={id} />;
}

function SidebarLink({ to, icon: Icon, label, accent, end, alertCount }) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}
      // The per-section accent is a CSS variable, not an inline border colour,
      // so the Stark theme can draw every active bar in reactor cyan.
      style={accent ? { '--nav-accent': accent } : undefined}
    >
      <Icon size={18} strokeWidth={2.2} aria-hidden="true" />
      <span>{label}</span>
      {alertCount > 0 && (
        <span className="nav-alert-count" title={`${alertCount} data source or storage alert(s) need attention`}>
          {alertCount}
        </span>
      )}
    </NavLink>
  );
}

/**
 * A collapsible nav section: one header row that behaves like a SidebarLink,
 * and a list of sub-items under it.
 *
 * Purely presentational — the caller resolves each item's `to` and `active`
 * before passing it down, because the three sections that use this resolve
 * differently: Contractors builds ?preset= links scoped to the selected state,
 * Bid Results builds them national, and Admin points at plain routes. Pushing
 * that into here would mean a prop for every one of those differences.
 *
 * Reuses the .smart-view-item / .smart-view-count classes the Contractors
 * accordion already defined; nothing in them was contractor-specific.
 *
 * @param {object[]} groups  [{ section?, items: [{ key, label, to, active, count, countClass }] }]
 *                           `section` is an optional divider label; omit it and
 *                           the group renders with no header.
 */
function NavExpander({
  icon: Icon, label, accent, count, countTone, active, open, onToggle, groups, onNavigate, children,
}) {
  const alert = countTone === 'alert';
  return (
    <div style={accent ? { '--nav-accent': accent } : undefined}>
      <div
        className={`nav-link ${active ? 'active' : ''}`}
        style={{ cursor: 'pointer', justifyContent: 'space-between' }}
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={onToggle}
        onKeyDown={(e) => onActivateKey(e, onToggle)}
      >
        <span className="nav-link-label">
          <Icon size={18} strokeWidth={2.2} aria-hidden="true" />
          <span>{label}</span>
        </span>
        <span className="nav-link-end">
          {count > 0 && (
            <span className={alert ? 'nav-alert-count' : 'nav-count'}>
              {count.toLocaleString()}
            </span>
          )}
          <ChevronDown size={14} strokeWidth={2.5} aria-hidden="true" className={`nav-chevron${open ? ' open' : ''}`} />
        </span>
      </div>

      {open && (
        <div style={{ paddingLeft: 0, overflow: 'hidden' }}>
          {groups.map((group, i) => (
            <div key={group.section || i}>
              {group.section && <div className="nav-sub-title">{group.section}</div>}
              {group.items.map((item) => (
                <div
                  key={item.key}
                  role="link"
                  tabIndex={0}
                  aria-current={item.active ? 'page' : undefined}
                  onClick={() => onNavigate(item.to)}
                  onKeyDown={(e) => onActivateKey(e, () => onNavigate(item.to))}
                  className={`smart-view-item${item.active ? ' active' : ''}`}
                >
                  <span>{item.label}</span>
                  {item.count != null && item.count > 0 && (
                    <span className={`smart-view-count${item.countClass ? ` ${item.countClass}` : ''}`}>
                      {item.count.toLocaleString()}
                    </span>
                  )}
                </div>
              ))}
            </div>
          ))}
          {children}
        </div>
      )}
    </div>
  );
}

/** Enter/Space on a div standing in for a button or link. */
function onActivateKey(e, fn) {
  if (e.target !== e.currentTarget) return;
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault();
    fn();
  }
}

function Sidebar({ mobileOpen, onClose }) {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  // Default to ALL states — the dataset is national. A specific state is
  // applied only when one is explicitly selected (?state=XX).
  const currentState = searchParams.get('state') || '';
  const theme = getTheme(currentState);
  const { user } = useAuth();
  // Nav is driven by the role capability map, not by a chain of `!isAssistant`
  // checks — those silently granted the whole app to any role that wasn't
  // literally 'assistant'.
  const nav = navFor(user);
  const isAdmin = user && user.role === 'admin';

  // Keep the selected state when jumping between pages
  // Takes extra params so a link that already carries a querystring doesn't end
  // up with two "?" — /dashboard?tab=reports needed that.
  const withState = (path, extra) => {
    const qs = new URLSearchParams(extra || {});
    if (currentState) qs.set('state', currentState);
    const s = qs.toString();
    return s ? `${path}?${s}` : path;
  };

  // Close the mobile drawer on every route change
  useEffect(() => { if (onClose) onClose(); }, [location.pathname, location.search]); // eslint-disable-line

  // Each expander starts open when you are already inside it, so a hard refresh
  // on /team lands with the Admin section showing where you are.
  const [contractorsOpen, setContractorsOpen] = useState(location.pathname.startsWith('/contractors'));
  const [bidOpen, setBidOpen] = useState(BID_PATHS.some((p) => location.pathname.startsWith(p)));
  const [adminOpen, setAdminOpen] = useState(ADMIN_PATHS.some((p) => location.pathname.startsWith(p)));
  const [viewCounts, setViewCounts] = useState({});
  const [scraperAlertCount, setScraperAlertCount] = useState(0);
  const [savedViews, setSavedViews] = useState([]);

  // Per-user saved views — kept in sync with the Contractors page via a
  // window event (and across tabs via the storage event).
  useEffect(() => {
    if (!user) return;
    const load = () => setSavedViews(getSavedViews(user.id));
    load();
    return onSavedViewsChange(load);
  }, [user]);

  // Fetch smart view counts. Skipped for roles without the Contractors section
  // — /contractors/counts is denied to them and the poll would 403 every minute.
  const showsContractors = nav.has('contractors');
  useEffect(() => {
    if (!showsContractors) return;
    const loadCounts = () => {
      fetch(`/api/contractors/counts?state=${currentState}`)
        .then(r => r.ok ? r.json() : {})
        .then(data => setViewCounts(data))
        .catch(() => {});
    };
    loadCounts();
    const interval = setInterval(loadCounts, 60000);
    return () => clearInterval(interval);
  }, [currentState, showsContractors]);

  // Scraper health: a red count on Data Sources when scrapers have failed or
  // gone silent — 21 cron jobs previously failed invisibly. Every 5 minutes.
  // Admins only: the badge this feeds is inside an admin-only section, so a
  // plain 'user' was polling every 5 minutes for a number nobody rendered.
  const showsAdmin = nav.has('admin') && isAdmin;
  useEffect(() => {
    if (!showsAdmin) return;
    const load = () => {
      fetch('/api/dashboard/scraper-alerts')
        .then(r => (r.ok ? r.json() : { total: 0 }))
        .then(d => setScraperAlertCount(d.total || 0))
        .catch(() => {});
    };
    load();
    const interval = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [showsAdmin]);

  // Keep a section open when navigating into it
  useEffect(() => {
    if (location.pathname.startsWith('/contractors')) setContractorsOpen(true);
    if (BID_PATHS.some((p) => location.pathname.startsWith(p))) setBidOpen(true);
    if (ADMIN_PATHS.some((p) => location.pathname.startsWith(p))) setAdminOpen(true);
  }, [location.pathname]);

  /**
   * Turn a preset definition into something NavExpander can render: a real
   * `to` and whether it is the view currently on screen.
   *
   * Two shapes. An item with `to` is a plain route (Admin, DOT Portal). An item
   * with `params` is a preset over `base` — its ?preset= is what lights the
   * highlight, since the filters themselves get rewritten by the page.
   */
  const resolveItem = (item, base, scopeToState) => {
    if (item.to) {
      return { ...item, to: item.keepState ? withState(item.to) : item.to, active: location.pathname === item.to };
    }
    const params = new URLSearchParams({ ...item.params, preset: item.key });
    // Only scope to a state when one is actually selected; otherwise the view
    // spans all states (national dataset).
    if (scopeToState && currentState) params.set('state', currentState);
    return {
      ...item,
      to: `${base}?${params.toString()}`,
      active: location.pathname === base && searchParams.get('preset') === item.key,
    };
  };

  const resolveGroups = (groups, base, scopeToState, counts = {}) => groups.map((g) => ({
    section: g.section,
    items: g.items.map((item) => ({
      ...resolveItem(item, base, scopeToState),
      count: item.countKey ? counts[item.countKey] : null,
    })),
  }));

  return (
    <nav
      className={`sidebar${mobileOpen ? ' mobile-open' : ''}`}
      aria-label="Main navigation"
      // The state's brand colours feed the Classic themes' sidebar header and
      // edge as CSS variables; the Stark theme ignores them for its own line work.
      style={{ '--state-primary': theme.colors.primary, '--state-secondary': theme.colors.secondary }}
    >
      {/* Sidebar Header */}
      <div className="sidebar-header">
        <div className="sidebar-brand">
          <ArcReactor size={36} className="brand-icon" />
          <div className="brand-text">
            <h1>BONDHQ</h1>
            <p>Surety Intelligence</p>
          </div>
        </div>
        {/* Global search spans the whole book — hidden for territory-locked
            roles, whose /dashboard/search request would be denied anyway. */}
        {!isProducer(user) && (
        <button
          type="button"
          className="sidebar-search"
          onClick={() => window.dispatchEvent(new Event('bondhq:open-palette'))}
        >
          <Search size={13} aria-hidden="true" />
          <span style={{ flex: 1, textAlign: 'left' }}>Search…</span>
          <span className="kbd-chip">Ctrl K</span>
        </button>
        )}
      </div>

      <div className="sidebar-nav">
        {/*
         * Three sections, grouped by WHEN you use them rather than by what the
         * pages are called.
         *
         * It was six, and three of them held a single link — a header, a
         * divider and one item, which is more chrome than content. "Bid
         * Intelligence" was a section header wrapping an expander labelled
         * "Bid Results", and "Admin & Data" a header wrapping an expander of
         * the same name.
         *
         * Every destination is still reachable and no route changed; only the
         * grouping did. roleAccess.js still decides visibility, so a role that
         * loses every item in a section never renders its header.
         */}

        {/* ── Work Today: the morning routine, in the order it happens ── */}
        <div className="nav-section">
          <div className="nav-section-title">Work Today</div>
          {/* Today and Command Center were two links to what is now one page,
              with Today as its default tab. FIRST in the section because it is
              the landing page — this is where the morning starts, whatever the
              label says. */}
          {nav.has('dashboard') && <SidebarLink to={withState('/dashboard')} icon={LayoutDashboard} label="Command Center" accent={theme.colors.primary} />}
          {nav.has('south-florida') && <SidebarLink to="/south-florida" icon={Waves} label="South Florida" accent="#0ea5e9" />}
          {nav.has('workspace') && <SidebarLink to={withState('/workspace')} icon={Send} label="Outreach Workspace" accent="#16a34a" />}
          {/* Pipeline is daily work for a producer, not a category of its own.
              It had a section header to itself.
              Deliberately not withState() — the board should open on All
              states by default. Pipeline.js still honours ?state= when a deep
              link (Today's "My Pipeline" chips, the board's own dropdown)
              sets it; the sidebar just no longer forces the app-wide state. */}
          {nav.has('pipeline') && <SidebarLink to="/pipeline" icon={Kanban} label="Pipeline" accent="#a855f7" />}
          {nav.has('pipeline') && <SidebarLink to="/jarvis" icon={Cpu} label="JARVIS" accent="#00e5ff" />}
          {nav.has('pipeline') && <SidebarLink to="/enhancements" icon={Lightbulb} label="Enhancements" accent="#facc15" />}
        </div>

        {/* ── Bid Data: where the numbers come from ── */}
        {(nav.has('bid-intel') || nav.has('contractors') || nav.has('map')) && (
        <div className="nav-section">
          <div className="nav-section-title">Bid Data</div>
          {/* Bid Results keeps its expander rather than becoming a flat link:
              a plain link plus the old section duplicated the same destination
              twice, and both entries lit up together. The presets (All
              Results, Rejected & Lost) live under it.
              Deliberately not withState() — the page is national by design,
              see BID_VIEWS. */}
          {nav.has('bid-intel') && (
          <NavExpander
            icon={Gavel}
            label="Bid Results"
            accent="#f59e0b"
            active={BID_PATHS.some((p) => location.pathname.startsWith(p))}
            open={bidOpen}
            onToggle={() => setBidOpen(o => !o)}
            groups={resolveGroups(BID_VIEWS, '/bid-results', false)}
            onNavigate={navigate}
          />
          )}
          {nav.has('contractors') && (
          <NavExpander
            icon={Users}
            label="Contractors"
            accent={theme.colors.secondary}
            count={viewCounts.all}
            active={location.pathname.startsWith('/contractors')}
            open={contractorsOpen}
            onToggle={() => setContractorsOpen(o => !o)}
            groups={resolveGroups(SMART_VIEWS, '/contractors', true, viewCounts)}
            onNavigate={navigate}
          >
            {/* Per-user saved views (created on the Contractors page). These
                carry a delete affordance, so they stay bespoke rather than
                being forced through the generic item contract. */}
            {savedViews.length > 0 && (
              <div>
                <div className="nav-sub-title">My Views</div>
                {savedViews.map((v) => {
                  const isActive = location.pathname === '/contractors' && location.search === `?${v.qs}`;
                  return (
                    <div
                      key={v.name}
                      role="link"
                      tabIndex={0}
                      aria-current={isActive ? 'page' : undefined}
                      onClick={() => navigate(`/contractors?${v.qs}`)}
                      onKeyDown={(e) => onActivateKey(e, () => navigate(`/contractors?${v.qs}`))}
                      className={`smart-view-item${isActive ? ' active' : ''}`}
                    >
                      <span>{v.name}</span>
                      <button
                        type="button"
                        className="smart-view-delete"
                        title="Delete this view"
                        aria-label={`Delete view ${v.name}`}
                        onClick={(e) => { e.stopPropagation(); removeView(user.id, v.name); }}
                      >
                        <X size={12} aria-hidden="true" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </NavExpander>
          )}
          {nav.has('map') && <SidebarLink to={withState('/map')} icon={MapPin} label="Contractor Map" accent="#0d9488" />}
        </div>
        )}

        {/* ── Tools & Admin: occasional, not daily ──
            The admin expander stays admin-gated on its own. Data Sources
            renders its own "Admin only" wall, and gating two of three children
            used to leave a header with a single orphan under it. */}
        {(nav.has('tools') || (nav.has('admin') && isAdmin)) && (
        <div className="nav-section">
          <div className="nav-section-title">Tools &amp; Admin</div>
          {nav.has('tools') && <SidebarLink to={withState('/bond-tools')} icon={Wrench} label="Bond Tools" accent="#0ea5e9" />}
          {nav.has('admin') && isAdmin && (
          <NavExpander
            icon={Database}
            label="Admin & Data"
            accent="#8b5cf6"
            count={scraperAlertCount}
            countTone="alert"
            active={ADMIN_PATHS.some((p) => location.pathname.startsWith(p))}
            open={adminOpen}
            onToggle={() => setAdminOpen(o => !o)}
            groups={resolveGroups(ADMIN_VIEWS, null, false, { scraperAlerts: scraperAlertCount })}
            onNavigate={navigate}
          />
          )}
        </div>
        )}
      </div>

      {/* Sidebar Footer */}
      <div className="sidebar-footer">
        <UserMenu />
      </div>
    </nav>
  );
}

function UserMenu() {
  const { user, logout } = useAuth();
  if (!user) return null;
  const initials = (user.name || user.email).split(/[\s@]/)[0].slice(0, 2).toUpperCase();
  return (
    <div className="user-menu">
      <div className="user-avatar" aria-hidden="true">{initials}</div>
      <div style={{ flex: 1, overflow: 'hidden' }}>
        <div className="user-name">{user.name || user.email}</div>
        <div className="user-role">{user.role}</div>
      </div>
      <button type="button" className="user-signout" onClick={logout} title="Sign out" aria-label="Sign out">
        <LogOut size={16} aria-hidden="true" />
      </button>
    </div>
  );
}

/**
 * Top bar of the authenticated shell: a compact system status strip
 * (connection · state in scope · local time), the palette trigger and the
 * theme switch. On phones it is the fixed top bar and carries the menu button.
 */
function ShellHeader({ onOpenMenu, showSearch, scopeLabel }) {
  const location = useLocation();
  const [online, setOnline] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine !== false));
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => { window.removeEventListener('online', up); window.removeEventListener('offline', down); };
  }, []);

  // Minute resolution is all the strip shows — tick on the minute, not every second.
  useEffect(() => {
    let interval;
    const timeout = setTimeout(() => {
      setNow(new Date());
      interval = setInterval(() => setNow(new Date()), 60000);
    }, 60000 - (Date.now() % 60000));
    return () => { clearTimeout(timeout); clearInterval(interval); };
  }, []);

  const stateCode = new URLSearchParams(location.search).get('state') || '';
  const stateName = stateCode ? (STATE_NAMES[stateCode] || stateCode) : '';
  const scope = scopeLabel || (stateCode ? `${stateCode} · ${stateName}` : 'All states');
  const time = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });

  return (
    <header className="shell-header">
      <button type="button" className="mobile-menu-toggle" aria-label="Open menu" onClick={onOpenMenu}>
        <Menu size={20} strokeWidth={2.5} aria-hidden="true" />
      </button>
      <div className="shell-header-mark" aria-hidden="true">
        <ArcReactor size={22} />
        <span className="shell-header-word">BONDHQ</span>
      </div>
      <div className="status-strip" aria-label={`System ${online ? 'online' : 'offline'}, viewing ${scope}`}>
        <span className="status-item">
          <span className={`status-dot${online ? '' : ' offline'}`} aria-hidden="true" />
          <span className="status-label">{online ? 'Sys online' : 'Offline'}</span>
        </span>
        <span className="status-sep" aria-hidden="true">·</span>
        <span className="status-item status-state">
          {scopeLabel || !stateCode ? scope : (
            <>{stateCode}<span className="status-state-name"> · {stateName}</span></>
          )}
        </span>
        <span className="status-sep status-sep-time" aria-hidden="true">·</span>
        <time className="status-item status-time" dateTime={now.toISOString()}>{time}</time>
      </div>
      <div className="shell-header-spacer" />
      <div className="shell-header-actions">
        {showSearch && (
          <button
            type="button"
            className="shell-search"
            aria-label="Search (Ctrl+K)"
            onClick={() => window.dispatchEvent(new Event('bondhq:open-palette'))}
          >
            <Search size={14} aria-hidden="true" />
            <span className="shell-search-label">Search</span>
            <span className="kbd-chip">Ctrl K</span>
          </button>
        )}
        <ThemeToggle />
      </div>
    </header>
  );
}

function AppContent() {
  const { user, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  // Per-user default state: remember the last state filter each user picked
  // and re-apply it when they come back with a bare URL — Taylor stops
  // re-selecting Georgia every single morning. An explicit "All states"
  // choice ('' after having had a value) is remembered too.
  const stateParam = new URLSearchParams(location.search).get('state');
  const prefKey = user ? `bondhq-default-state:${user.id}` : null;
  const appliedRef = useRef(false);
  useEffect(() => {
    if (!prefKey) return;
    // Territory-locked roles have no state switcher and their scope comes from
    // the server — don't let a remembered ?state= follow them onto their pages.
    if (isProducer(user)) return;
    if (stateParam !== null) {
      // User navigated with an explicit state (or cleared it) — remember it.
      localStorage.setItem(prefKey, stateParam);
      appliedRef.current = true;
    } else if (!appliedRef.current) {
      // First bare-URL load of the session: apply the remembered state once.
      appliedRef.current = true;
      const saved = localStorage.getItem(prefKey);
      if (saved) {
        navigate({ pathname: location.pathname, search: `?state=${saved}` }, { replace: true });
      }
    }
  }, [prefKey, stateParam, location.pathname, navigate, user]);

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <ArcLoader label="Starting BondHQ" />
      </div>
    );
  }
  if (!user) return <Login />;

  const home = homeFor(user);

  // Territory-locked roles get their own route table with a catch-all, rather
  // than a guard bolted onto each of the ~30 routes below — you cannot forget
  // to guard a route that isn't in the list. The server enforces the same
  // boundary; this just keeps the UI from offering doors that won't open.
  if (isProducer(user)) {
    return (
      <div className="app-layout">
        <Sidebar mobileOpen={mobileSidebarOpen} onClose={() => setMobileSidebarOpen(false)} />
        <div className="sidebar-backdrop" onClick={() => setMobileSidebarOpen(false)} style={{ display: mobileSidebarOpen ? 'block' : 'none' }} />
        <main className="main-content">
          {/* Territory-locked: the server scopes every query, so the strip
              names the lock rather than a ?state= the producer cannot change. */}
          <ShellHeader onOpenMenu={() => setMobileSidebarOpen(true)} showSearch={false} scopeLabel="Territory" />
          <ErrorBoundary resetKey={location.pathname}>
          <Suspense fallback={<ArcLoader />}>
          <Routes>
            <Route path="/south-florida" element={<SouthFlorida />} />
            <Route path="/map" element={<ContractorMap />} />
            <Route path="/contractors/:id" element={<ProducerContractor />} />
            <Route path="*" element={<Navigate to="/south-florida" replace />} />
          </Routes>
          </Suspense>
          </ErrorBoundary>
        </main>
      </div>
    );
  }

  return (
    <div className="app-layout">
      <Sidebar mobileOpen={mobileSidebarOpen} onClose={() => setMobileSidebarOpen(false)} />
      <div className="sidebar-backdrop" onClick={() => setMobileSidebarOpen(false)} style={{ display: mobileSidebarOpen ? 'block' : 'none' }} />
      <CommandPalette role={user.role} />
      <main className="main-content">
        <ShellHeader onOpenMenu={() => setMobileSidebarOpen(true)} showSearch />
        {/* Error boundary keyed on the path: a page that throws shows a
            recoverable message instead of blanking the whole app, and
            navigating elsewhere auto-clears it. */}
        <ErrorBoundary resetKey={location.pathname}>
        <Suspense fallback={<ArcLoader />}>
        <Routes>
          <Route path="/" element={<Navigate to={home} replace />} />
          <Route path="/home" element={<Navigate to={home} replace />} />
          <Route path="/workspace" element={<OutreachWorkspace />} />
          <Route path="/team" element={user.role === 'admin' ? <Team /> : <Navigate to="/workspace" replace />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/dot-portal" element={<DOTPortal />} />
          <Route path="/contractors" element={<Contractors />} />
          <Route path="/contractors/:id" element={<ContractorProfileRoute />} />
          <Route path="/map" element={<ContractorMap />} />
          {/* Retired pages — folded into DOT Portal / Contractors smart views */}
          <Route path="/opportunities" element={<Navigate to="/dot-portal" replace />} />
          <Route path="/data-sources" element={<AdminDataSources />} />
          <Route path="/import-contractors" element={<ImportContractors />} />
          <Route path="/lettings/:id" element={<LettingDetail />} />
          <Route path="/prospects" element={<Navigate to="/contractors?preset=hot&lead_priority=hot&is_client=0&sort_by=lead_score&sort_dir=desc" replace />} />
          <Route path="/bid-results" element={<BidResultsRoute />} />
          <Route path="/bond-forms" element={<BondForms />} />
          <Route path="/bond-forms/new" element={<BondFormNew />} />
          <Route path="/bond-forms/:id" element={<BondFormEditor />} />
          <Route path="/bond-tools" element={<BondTools />} />
          <Route path="/bond-tools/calculator" element={<BondCalculator />} />
          <Route path="/bond-tools/letters" element={<BondLetters />} />
          <Route path="/bond-tools/wip-analyzer" element={<WipAnalyzer />} />
          <Route path="/bond-tools/financial-review" element={<FinancialReview />} />
          <Route path="/bond-tools/ar-tracker" element={<ArTracker />} />
          <Route path="/pipeline" element={<Pipeline />} />
          <Route path="/jarvis" element={<JarvisPage />} />
          <Route path="/jarvis/memory" element={<JarvisMemory />} />
          <Route path="/jarvis/voice" element={<JarvisVoice />} />
          <Route path="/enhancements" element={<Enhancements />} />
          {/* Today is a tab of the Command Center now. Redirect rather than
              drop the route: /today is the old home page, so it is bookmarked,
              typed, and linked from toasts. `replace` keeps it out of history
              so Back does not bounce between the two. */}
          <Route path="/today" element={<TodayRedirect />} />
          {/* Legacy redirects */}
          <Route path="/admin/data-sources" element={<Navigate to="/data-sources" replace />} />
          <Route path="/lettings" element={<Navigate to="/dot-portal" replace />} />
          <Route path="/plan-pullers" element={<Navigate to="/dot-portal" replace />} />
          <Route path="/scraper" element={<Navigate to="/data-sources" replace />} />
          <Route path="/analytics" element={<Navigate to="/dot-portal" replace />} />
          <Route path="/rejected-bids" element={<Navigate to="/bid-results?mode=bidders&bid_status=rejected%2Cnon-responsive&preset=rejected" replace />} />
          <Route path="/reports" element={<Navigate to="/dashboard?tab=reports" replace />} />
          {/* The producer's workspace, also reachable by admins and agents so
              the view can be checked without a producer login. Same page, same
              server-scoped data — only the sidebar and the lock differ. */}
          <Route path="/south-florida" element={<SouthFlorida />} />
        </Routes>
        </Suspense>
        </ErrorBoundary>
      </main>
      {/* JARVIS: floats over every authenticated (non-producer) page. Ctrl+J
          toggles it; the full-screen view lives at /jarvis. */}
      <JarvisDock />
    </div>
  );
}

function App() {
  // public/index.html already applied the saved theme before first paint;
  // this re-asserts it in case the inline script could not run.
  useEffect(() => {
    applyTheme(getInitialTheme());
  }, []);
  return (
    <ToastProvider>
      <ConfirmProvider>
        <AuthProvider>
          <Router>
            <AppContent />
          </Router>
        </AuthProvider>
      </ConfirmProvider>
    </ToastProvider>
  );
}

export default App;
